import React, { useCallback, useState, useEffect } from 'react'

// styles
import styles from './index.module.scss'
import Image from 'next/image'
import { isString } from 'utils/helpers'

function shouldUseHeliusCdn(url: URL): boolean {
  let hostname = url.hostname
  if (hostname == 'cdn.helius-rpc.com') return false

  return true
}

function useHeliusCdn(urlString: string | null, options: Record<string, string | number> = {}): string | null {
  try {
    if (!urlString || !isString(urlString) || !urlString.match(/^https?:\/\//i)) return null

    // unwrap cdn urls
    let url = new URL(urlString)
    while (url.hostname == 'cdn.helius-rpc.com' && url.pathname.startsWith('/cdn-cgi/image/')) {
      try {
        url = new URL(decodeURIComponent(url.pathname.match(/(?<=\/cdn-cgi\/image\/[^\/]*\/).*$/)[0]))
      } catch (e: any) {
        console.error(`could not decode url: ${url.toString()}, original: ${urlString}: ${e?.message}`)
        return null
      }
    }

    if (!shouldUseHeliusCdn(url)) return null

    let parameters = Object.entries(options)
      .filter(([, v]) => v !== undefined && v !== null)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => k + '=' + v.toString())
      .join(',')
    return `https://cdn.helius-rpc.com/cdn-cgi/image/${parameters}/${encodeURIComponent(url.toString())}`
  } catch (error) {
    console.log(error)
  }
}

export type ImageWithFosterProps = {
  src: string
  link?: string
  quality?: number
  originalSrc?: string | null
  showPlaceHolder?: boolean
  showNftMode?: boolean
  useNextImage?: boolean
  placeholder?: 'blur' | 'empty'
} & React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>

const ImageWithFoster = ({
  src,
  link = '',
  originalSrc = null,
  showPlaceHolder = true,
  showNftMode = false,
  quality = 75,
  width,
  height,
  onClick,
  onLoad,
  onError,
  useNextImage,
  ...rest
}: ImageWithFosterProps) => {
  const [loaded, setLoaded] = useState<boolean>(false)
  const [fadeIn, setFadeIn] = useState<boolean>(false)
  const [isCached, setIsCached] = useState(false)
  const [hasError, setHasError] = useState(false)

  const heliusUrl = useHeliusCdn(src, {
    quality,
    width,
    height,
  })

  const srcSpecs = [
    { url: heliusUrl, useNextImage: false },
    { url: src, useNextImage: true },
    { url: originalSrc, useNextImage: true },
    { url: heliusUrl, useNextImage: true },
    { url: src, useNextImage: false },
    { url: originalSrc, useNextImage: false },
  ].filter(spec => spec.url && (useNextImage === undefined || useNextImage === spec.useNextImage))

  const [currentSrcSpecIndex, setCurrentSrcSpecIndex] = useState<number>(0)

  const handleLoad = useCallback(
    e => {
      setLoaded(true)
      setFadeIn(true)
      return onLoad?.(e)
    },
    [onLoad]
  )

  const handleError = useCallback(
    e => {
      setHasError(true)
      let newIndex = currentSrcSpecIndex + 1
      if (newIndex < srcSpecs.length) {
        setCurrentSrcSpecIndex(newIndex)
        setHasError(false)
      } else {
        return onError?.(e)
      }
    },
    [srcSpecs, currentSrcSpecIndex, onError]
  )

  useEffect(() => {
    if (srcSpecs[currentSrcSpecIndex]?.url && !hasError) {
      const img = new window.Image()
      img.src = srcSpecs[currentSrcSpecIndex].url
      if (img.complete) {
        setIsCached(true)
        handleLoad({ target: img })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSrcSpecIndex, srcSpecs, hasError])

  useEffect(() => {
    if (loaded) {
      setTimeout(() => {
        setFadeIn(true)
      }, 100)
    }
  }, [loaded])

  return (
    <div className="w-full h-full relative overflow-hidden" onClick={onClick}>
      <div className="w-full h-full flex justify-center items-center z-1000">
        <div
          className={`
            w-full h-full flex justify-center items-center
            transition-opacity duration-1000 ease-in-out
            ${fadeIn ? 'opacity-100' : 'opacity-0'}
          `}
        >
          {srcSpecs[currentSrcSpecIndex]?.useNextImage ? (
            showNftMode ? (
              <div className={styles.mobileContainer}>
                <Image
                  src={srcSpecs[currentSrcSpecIndex]?.url}
                  onLoadingComplete={handleLoad}
                  onLoad={handleLoad}
                  loading="lazy"
                  layout="fill"
                  alt=""
                  onError={handleError}
                  {...rest}
                />
              </div>
            ) : (
              <Image
                src={srcSpecs[currentSrcSpecIndex]?.url}
                onLoadingComplete={handleLoad}
                onLoad={handleLoad}
                loading="lazy"
                layout="fill"
                alt=""
                onError={handleError}
                {...rest}
              />
            )
          ) : srcSpecs[currentSrcSpecIndex]?.url ? (
            <img
              src={srcSpecs[currentSrcSpecIndex]?.url}
              onLoad={handleLoad}
              loading="lazy"
              onError={handleError}
              alt=""
              {...rest}
            />
          ) : (
            <div></div>
          )}
        </div>
      </div>
      <div className="w-full h-full absolute top-0 overflow-hidden" onClick={onClick}>
        <div
          className={`${styles.skeletonLoaderOuterBox} absolute z-50 top-0 transition-all ${
            loaded || (!showPlaceHolder && !showNftMode) ? 'hidden' : 'visible'
          }`}
        >
          <div className={styles.skeletonLoader} />
        </div>
      </div>
    </div>
  )
}

export default ImageWithFoster
